import { useMemo, useRef } from 'react';
import type { Signer } from '@cef-ai/signer';
import { Box, Typography } from '@cere/cere-design-system';
import { useWidgetSignBridge } from '@/lib/vault/widget-sign-bridge/use-widget-sign-bridge';
import { useWidgetSignConsent } from '@/lib/vault/widget-sign-bridge/use-widget-sign-consent';
import { isSafeWidgetEmbedUrl, resolveWidgetUrl } from '@/lib/vault/widget-embed-url';
import { WidgetSignConsentDialog } from '@/src/components/WidgetSignConsentDialog';
import type { Widget } from '@/lib/manifest/types';

interface Props {
  widget: Widget;
  /**
   * Signer whose publicKey/sign capability the iframe is bridged to via
   * postMessage. Pass `null` if the user isn't connected; the widget will
   * load but its identity/sign requests time out (the widget's own error UI
   * takes over).
   */
  signer: Signer | null;
  /**
   * DDC content-gateway base for resolving `cef push` widgets that carry a
   * `dag`/`entry` instead of an `embedUrl`. Unused for embedUrl widgets.
   */
  cdnBaseUrl?: string;
}

/**
 * Inline widget embed used on the agent detail page. The sign bridge is
 * mounted for the lifetime of this component — i.e. for as long as the user
 * is on the agent's page. Unmounts on navigation away. Publisher code runs
 * inside the iframe sandbox.
 */
export function WidgetEmbed({ widget, signer, cdnBaseUrl = '' }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Resolve the loadable URL first: an explicit embedUrl, or the DDC gateway
  // URL built from the widget's dag/entry (`cef push` shape). '' when neither
  // is usable (e.g. an unpublished build), which fails the safety gate below.
  const url = useMemo(() => resolveWidgetUrl(widget, cdnBaseUrl), [widget, cdnBaseUrl]);
  // Same defense-in-depth as WidgetIframeModal: refuse to render a URL that is
  // non-https or resolves to the vault's own origin. `sandbox="allow-same-origin"`
  // would otherwise let a same-origin frame inherit the vault's cookies /
  // localStorage / parent DOM — gating only the bridge is not enough.
  const safe = useMemo(
    () => url !== '' && isSafeWidgetEmbedUrl(url, window.location.origin),
    [url],
  );
  // One-time, remembered consent gates signing; the bridge persists the
  // grant on approval (see useWidgetSignBridge). Skip the bridge entirely
  // for an unsafe URL — there's no iframe to answer.
  const consent = useWidgetSignConsent();
  useWidgetSignBridge(iframeRef, safe ? signer : null, consent.requestConsent);

  const title = widget.name?.trim() || widget.id;
  const description = widget.description?.trim();

  return (
    <Box className="vault-detail-widget-embed">
      <Box className="vault-detail-widget-embed__head">
        <Typography variant="panelTitle">{title}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box className="vault-detail-widget-embed__frame">
        {safe ? (
          <iframe
            ref={iframeRef}
            src={url}
            title={title}
            // `flex: 1` (not height:100%) so the iframe fills the flex-column
            // frame — a parent whose height comes from flex has computed
            // `height: auto`, against which a percentage height collapses to
            // the ~150px iframe default.
            style={{ width: '100%', flex: 1, minHeight: 0, border: 0, display: 'block' }}
            allow="microphone; display-capture; clipboard-write; fullscreen"
            referrerPolicy="no-referrer"
            // Publisher-controlled URL: scripts run + same-origin needed for
            // the widget's own VaultSDK fetches, but omit `allow-top-navigation`
            // so a malicious widget cannot reroute window.top to a phishing
            // page. `allow-popups` keeps OAuth-style flows the widget might
            // need available; `allow-downloads` lets the widget trigger
            // file saves (blob/Content-Disposition) without a new tab.
            sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            This widget cannot be embedded. Its publisher-declared embed URL does
            not pass the vault&apos;s same-origin / https-only safety check.
          </Typography>
        )}
      </Box>
      <WidgetSignConsentDialog
        open={consent.dialogOpen}
        onAllow={consent.allow}
        onDeny={consent.deny}
      />
    </Box>
  );
}
